import React, { createContext, useContext, useState, useEffect } from 'react';
import localApiAdapter from '../services/localApiAdapter';
import i18n from '../../i18n';

const rt = (key, opts) => i18n.t(`serviceMessages.returns.${key}`, opts);

const ReturnsContext = createContext();

export const useReturns = () => {
  const context = useContext(ReturnsContext);
  if (!context) {
    throw new Error(rt('hook_outside_provider'));
  }
  return context;
};

export const ReturnsProvider = ({ children }) => {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch returns from database
  const fetchReturns = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch returns from local database
      const returnsData = await localApiAdapter.documents.getDocuments();
      
      // Filter for return-slip documents only
      const returnDocuments = returnsData.filter(doc => doc.type === 'return_slip' || doc.type === 'return-slip');
      
      // Convert documents to return format
      const formattedReturns = returnDocuments.map(doc => ({
        id: doc.id,
        returnNumber: doc.number || `RT-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
        type: doc.supplier_id ? 'supplier' : 'customer',
        entityId: doc.client_id || doc.supplier_id,
        entityName: doc.client_name || doc.supplier_name || doc.client,
        date: doc.date || new Date().toISOString().split('T')[0],
        amount: parseFloat(doc.amount || doc.total || 0),
        status: doc.status || 'pending',
        reason: doc.return_reason || doc.returnReason || 'other',
        refundMethod: doc.refund_method || doc.refundMethod || 'credit',
        items: doc.items || [],
        notes: doc.notes || '',
        createdAt: doc.created_at || new Date().toISOString(),
        updatedAt: doc.updated_at || new Date().toISOString()
      }));
      
      setReturns(formattedReturns);
      console.log(`?? Loaded ${formattedReturns.length} returns from database`);
      
    } catch (error) {
      console.error('Failed to fetch returns from database:', error);
      setError(rt('load_failed'));
      setReturns([]);
    } finally {
      setLoading(false);
    }
  };

  // Add a new return
  const addReturn = async (returnData) => {
    try {
      // Convert return data to document format
      const documentData = {
        type: 'return_slip',
        number: returnData.returnNumber || `RT-${String(returns.length + 1).padStart(3, '0')}`,
        client_id: returnData.type === 'customer' ? returnData.entityId : null,
        supplier_id: returnData.type === 'supplier' ? returnData.entityId : null,
        client_name: returnData.type === 'customer' ? returnData.entityName : null,
        supplier_name: returnData.type === 'supplier' ? returnData.entityName : null,
        date: returnData.date || new Date().toISOString().split('T')[0],
        amount: returnData.amount || 0,
        total: returnData.amount || 0,
        status: returnData.status || 'pending',
        return_reason: returnData.reason || 'other',
        refund_method: returnData.refundMethod || 'credit',
        items: returnData.items || [],
        notes: returnData.notes || '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Save to database
      const createdDoc = await localApiAdapter.documents.createDocument(documentData);
      
      // Refresh returns data
      await fetchReturns();
      
      return createdDoc;
    } catch (error) {
      console.error('Error adding return:', error);
      setError(rt('add_failed'));
      throw error;
    }
  };

  // Update an existing return
  const updateReturn = async (id, returnData) => {
    try {
      // Convert return data to document format for update
      const updateData = {
        status: returnData.status,
        return_reason: returnData.reason,
        refund_method: returnData.refundMethod,
        notes: returnData.notes,
        amount: returnData.amount,
        total: returnData.amount,
        updated_at: new Date().toISOString(),
        ...returnData
      };

      // Update in database
      await localApiAdapter.documents.updateDocument(id, updateData);
      
      // Refresh returns data
      await fetchReturns();
      
      return returns.find(ret => ret.id === id);
    } catch (error) {
      console.error('Error updating return:', error);
      setError(rt('update_failed'));
      throw error;
    }
  };

  // Delete a return
  const deleteReturn = async (id) => {
    try {
      // Delete from database
      await localApiAdapter.documents.deleteDocument(id);
      
      // Refresh returns data
      await fetchReturns();
    } catch (error) {
      console.error('Error deleting return:', error);
      setError(rt('delete_failed'));
      throw error;
    }
  };

  // Get return by ID
  const getReturnById = (id) => {
    return returns.find(ret => ret.id === id);
  };

  // Get returns by status
  const getReturnsByStatus = (status) => {
    return returns.filter(ret => ret.status === status);
  };

  // Get returns by type (customer/supplier)
  const getReturnsByType = (type) => {
    return returns.filter(ret => ret.type === type);
  };

  // Calculate return statistics
  const getReturnStats = () => {
    const customerReturns = returns.filter(ret => ret.type === 'customer');
    const supplierReturns = returns.filter(ret => ret.type === 'supplier');

    return {
      total: returns.length,
      customer: {
        total: customerReturns.length,
        pending: customerReturns.filter(ret => ret.status === 'pending').length,
        accepted: customerReturns.filter(ret => ret.status === 'accepted').length,
        rejected: customerReturns.filter(ret => ret.status === 'rejected').length,
        totalAmount: customerReturns.reduce((sum, ret) => sum + (ret.amount || 0), 0),
      },
      supplier: {
        total: supplierReturns.length,
        pending: supplierReturns.filter(ret => ret.status === 'pending').length,
        completed: supplierReturns.filter(ret => ret.status === 'completed').length,
        rejected: supplierReturns.filter(ret => ret.status === 'rejected').length,
        totalAmount: supplierReturns.reduce((sum, ret) => sum + (ret.amount || 0), 0),
      },
      byReason: {
        defective: returns.filter(ret => ret.reason === 'defective').length,
        wrongItem: returns.filter(ret => ret.reason === 'wrong_item').length,
        damaged: returns.filter(ret => ret.reason === 'damaged').length,
        expired: returns.filter(ret => ret.reason === 'expired').length,
        other: returns.filter(ret => ret.reason === 'other').length,
      }
    };
  };

  // Create return from document (bordereau de retour)
  const createReturnFromDocument = async (documentData) => {
    try {
      const returnData = {
        id: crypto.randomUUID(),
        returnNumber: documentData.number,
        type: documentData.supplier ? 'supplier' : 'customer',
        entityId: documentData.supplier || documentData.client,
        entityName: documentData.supplierName || documentData.clientName,
        date: documentData.date,
        amount: documentData.total || 0,
        status: 'pending',
        reason: documentData.return?.reason || 'other',
        refundMethod: documentData.return?.refundMethod || 'credit',
        items: documentData.items || [],
        notes: documentData.notes || '',
        originalInvoiceRef: documentData.return?.originalInvoiceRef || '',
        originalDeliveryRef: documentData.return?.originalDeliveryRef || '',
        documentId: documentData.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      return await addReturn(returnData);
    } catch (error) {
      console.error('Error creating return from document:', error);
      throw error;
    }
  };

  // Clear all returns (for testing/reset)
  const clearAllReturns = async () => {
    try {
      // Get all return documents
      const allReturns = returns;
      
      // Delete each return from database
      for (const returnItem of allReturns) {
        await localApiAdapter.documents.deleteDocument(returnItem.id);
      }
      
      // Refresh data
      await fetchReturns();
      console.log('?? All returns cleared from database');
    } catch (error) {
      console.error('Error clearing returns:', error);
    }
  };

  // Initialize data on mount
  useEffect(() => {
    fetchReturns();
  }, []);

  const value = {
    returns,
    loading,
    error,
    // CRUD operations
    addReturn,
    updateReturn,
    deleteReturn,
    // Getters
    getReturnById,
    getReturnsByStatus,
    getReturnsByType,
    getReturnStats,
    // Utilities
    fetchReturns,
    createReturnFromDocument,
    clearAllReturns,
  };

  return (
    <ReturnsContext.Provider value={value}>
      {children}
    </ReturnsContext.Provider>
  );
};

export default ReturnsContext;


